import { toTimeInput } from '@/components/campaigns/campaignFormat'
import { hasDynamicUrlButton, templateVariablePositions } from '@/components/campaigns/campaignTemplates'
import type { CampaignSettings, CampaignStep } from '@/services/crmCampaignsService'
import type { WhatsAppTemplate } from '@/services/whatsappService'

export type SettingsErrors = Partial<Record<'hora_fin' | 'dias_laborables' | 'limite_diario' | 'limite_minuto', string>>

export function validateSettings(draft: CampaignSettings, t: (key: string) => string): SettingsErrors {
  const errors: SettingsErrors = {}
  if (!draft.hora_inicio || !draft.hora_fin || toTimeInput(draft.hora_inicio) >= toTimeInput(draft.hora_fin)) {
    errors.hora_fin = t('campaigns.settings.error_window')
  }
  if (draft.dias_laborables.length === 0) errors.dias_laborables = t('campaigns.settings.error_days')
  if (!(draft.limite_diario >= 1)) errors.limite_diario = t('campaigns.settings.error_limit')
  if (!(draft.limite_minuto >= 1)) errors.limite_minuto = t('campaigns.settings.error_limit')
  return errors
}

export type StepErrors = Partial<Record<'template' | 'day' | 'time' | 'link', string>> & {
  variables?: Record<number, string>
}

export function hasStepErrors(errors: StepErrors): boolean {
  return Boolean(
    errors.template || errors.day || errors.time || errors.link || Object.keys(errors.variables ?? {}).length,
  )
}

export function validateSteps(
  steps: CampaignStep[],
  templates: WhatsAppTemplate[],
  window: { start: string; end: string },
  t: (key: string, values?: Record<string, unknown>) => string,
): StepErrors[] {
  return steps.map((step, index) => {
    const errors: StepErrors = {}
    const template = templates.find(
      (item) => item.name === step.plantilla_nombre && item.language === step.plantilla_idioma,
    )
    if (!template) errors.template = t('campaigns.steps.error_template')

    const previous = steps[index - 1]
    if (!Number.isInteger(step.dia_offset) || step.dia_offset < 0) {
      errors.day = t('campaigns.steps.error_day')
    } else if (previous && step.dia_offset < previous.dia_offset) {
      errors.day = t('campaigns.steps.error_day_order')
    }

    if (!step.hora_envio || step.hora_envio < window.start || step.hora_envio >= window.end) {
      errors.time = t('campaigns.steps.error_time', { start: window.start, end: window.end })
    }

    if (hasDynamicUrlButton(template) && !step.enlace_buscador) {
      errors.link = t('campaigns.steps.error_link')
    }

    const variables: Record<number, string> = {}
    for (const position of templateVariablePositions(template)) {
      const entry = step.mapeo_variables.find((item) => item.position === position)
      if (!entry?.source_key.trim()) {
        variables[position] = t('campaigns.steps.error_variable', { position })
      }
    }
    if (Object.keys(variables).length > 0) errors.variables = variables
    return errors
  })
}
